import React from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography
} from '@mui/material';

import { useStreetSyncPreview, useApplyStreetSync } from '@api';
import type { StreetSyncPreview } from '@api';

/**
 * Pull a Street account's branches and staff in — preview first.
 *
 * Opening the dialog reads Street (nothing is written). The admin sees how many
 * branches and users the account has and exactly what would be created, and
 * only Confirm writes. The server re-reads Street on confirm, so the result
 * reports what was actually done rather than echoing the preview.
 */

interface StreetSyncDialogProps {
  open: boolean;
  companyId: string;
  /** Shown above the counts when the dialog opened itself after a first token save. */
  intro?: string;
  onClose: () => void;
  onSynced?: () => void;
}

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

const Stat: React.FC<{ value: number; label: string }> = ({ value, label }) => (
  <Box sx={{ flex: 1, p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
    <Typography variant="h5">{value}</Typography>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
  </Box>
);

const Line: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography variant="body2" component="li" sx={{ mb: 0.5 }}>
    {children}
  </Typography>
);

const PreviewBody: React.FC<{ preview: StreetSyncPreview }> = ({ preview }) => {
  const { branches, users } = preview;
  const activeStaff = users.total - users.deactivated;

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={1.5}>
        <Stat value={branches.total} label="branches in Street" />
        <Stat value={activeStaff} label={`active users in Street${users.deactivated ? ` (+${users.deactivated} leavers)` : ''}`} />
      </Stack>

      <Box>
        <Typography variant="subtitle2" gutterBottom>
          Branches → teams
        </Typography>
        <Box component="ul" sx={{ pl: 2.5, m: 0 }}>
          <Line>{plural(branches.toCreate.length, 'new team')} to create{branches.toCreate.length ? `: ${branches.toCreate.map((b) => b.teamName).join(', ')}` : ''}</Line>
          {branches.toAdopt.length > 0 && (
            <Line>
              {plural(branches.toAdopt.length, 'existing team')} linked by matching name:{' '}
              {branches.toAdopt.map((b) => b.teamName).join(', ')}
            </Line>
          )}
          <Line>{plural(branches.alreadyMapped, 'branch', 'branches')} already mapped — left as they are, and marked confirmed</Line>
        </Box>
      </Box>

      {branches.renamedInCrm.length > 0 && (
        <Alert severity="info">
          Renamed in Street since they were mapped:{' '}
          {branches.renamedInCrm.map((r) => `${r.teamName} → ${r.branchName}`).join(', ')}. Your team names are kept —
          rename them yourself if you want them to match.
        </Alert>
      )}
      {branches.stale.length > 0 && (
        <Alert severity="warning">
          {plural(branches.stale.length, 'team')} mapped to a branch Street no longer lists:{' '}
          {branches.stale.map((t) => t.teamName).join(', ')}. Nothing will change — unmap or remap{' '}
          {branches.stale.length === 1 ? 'it' : 'them'} when you know why.
        </Alert>
      )}

      <Box>
        <Typography variant="subtitle2" gutterBottom>
          Users
        </Typography>
        <Box component="ul" sx={{ pl: 2.5, m: 0 }}>
          <Line>{plural(users.toCreate.length, 'new user')} to create, each in their branch&apos;s team</Line>
          <Line>{plural(users.alreadyInAccount, 'user')} already in this account{users.toAssignTeam ? ` — ${users.toAssignTeam} without a team will get their branch's team` : ''}</Line>
          {users.deactivated > 0 && <Line>{plural(users.deactivated, 'leaver')} in Street — skipped</Line>}
          {users.withoutEmail > 0 && <Line>{plural(users.withoutEmail, 'user')} with no email — skipped</Line>}
        </Box>
      </Box>

      {users.inOtherAccount.length > 0 && (
        <Alert severity="warning">
          {plural(users.inOtherAccount.length, 'email')} already belong to another SalesRook account and will be
          skipped: {users.inOtherAccount.map((u) => u.email).join(', ')}
        </Alert>
      )}
      {users.previouslyDeleted.length > 0 && (
        <Alert severity="warning">
          {plural(users.previouslyDeleted.length, 'email')} belonged to a deleted user and cannot be reused:{' '}
          {users.previouslyDeleted.map((u) => u.email).join(', ')}
        </Alert>
      )}

      {users.toCreate.length > 0 && (
        <Alert severity="info">
          No invitation emails are sent. New users can sign in with a magic link straight away; each is activated on
          first sign-in, subject to the account&apos;s licensed seats.
        </Alert>
      )}
    </Stack>
  );
};

export const StreetSyncDialog: React.FC<StreetSyncDialogProps> = ({ open, companyId, intro, onClose, onSynced }) => {
  const preview = useStreetSyncPreview(companyId, open);
  const apply = useApplyStreetSync(companyId);

  const close = () => {
    apply.reset();
    onClose();
  };

  const plan = preview.data;
  const nothingToCreate =
    plan &&
    plan.branches.toCreate.length + plan.branches.toAdopt.length + plan.users.toCreate.length + plan.users.toAssignTeam === 0;
  // With nothing new, confirming still records that the existing mappings were
  // checked — what "Check against Street" used to do. With no mappings either,
  // there is genuinely nothing to press.
  const nothingToDo = nothingToCreate && plan.branches.alreadyMapped === 0;
  const result = apply.data;

  return (
    <Dialog open={open} onClose={apply.isPending ? undefined : close} maxWidth="sm" fullWidth>
      <DialogTitle>Sync from Street</DialogTitle>
      <DialogContent>
        {intro && !result && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {intro}
          </Typography>
        )}

        {preview.isLoading && (
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 3 }}>
            <CircularProgress size={20} />
            <Typography variant="body2">Reading branches and users from Street…</Typography>
          </Stack>
        )}

        {preview.isError && <Alert severity="error">{(preview.error as Error).message}</Alert>}

        {plan && !result && <PreviewBody preview={plan} />}

        {plan && !result && nothingToCreate && (
          <Alert severity="success" sx={{ mt: 2 }}>
            Everything in Street is already here — nothing new to create.
          </Alert>
        )}

        {result && (
          <Stack spacing={1.5}>
            <Alert severity="success">
              {plural(result.teams.created.length, 'team')} created
              {result.teams.adopted.length ? `, ${result.teams.adopted.length} linked` : ''}
              {result.teams.confirmed ? `, ${result.teams.confirmed} confirmed` : ''} ·{' '}
              {plural(result.users.created, 'user')} created
              {result.users.teamAssigned ? ` · ${result.users.teamAssigned} assigned to a team` : ''}
            </Alert>
            {[...result.teams.skipped.map((s) => `${s.name}: ${s.reason}`), ...result.users.skipped.map((s) => `${s.email}: ${s.reason}`)]
              .length > 0 && (
              <Alert severity="warning">
                Skipped —{' '}
                {[
                  ...result.teams.skipped.map((s) => `${s.name}: ${s.reason}`),
                  ...result.users.skipped.map((s) => `${s.email}: ${s.reason}`)
                ].join(' · ')}
              </Alert>
            )}
          </Stack>
        )}

        {apply.isError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {(apply.error as Error).message}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        {result ? (
          <Button variant="contained" onClick={close}>
            Done
          </Button>
        ) : (
          <>
            <Button onClick={close} disabled={apply.isPending}>
              {intro ? 'Not now' : 'Cancel'}
            </Button>
            <Button
              variant="contained"
              disabled={!plan || nothingToDo || apply.isPending}
              onClick={() => apply.mutate(undefined, { onSuccess: () => onSynced?.() })}
            >
              {apply.isPending
                ? 'Syncing…'
                : plan && !nothingToCreate
                  ? `Create ${plural(plan.branches.toCreate.length, 'team')} and ${plural(plan.users.toCreate.length, 'user')}`
                  : 'Confirm mappings'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default StreetSyncDialog;
